import { css, cva } from "@stubhub/broadway-foundation/css";

/**
 * Recipe for the outer cell container.
 * Replaces `cx(sharedCellBase, emptyDayCellStyle)` and
 * `cx(sharedCellBase, containerWithEventsStyle, expanded ? …)`.
 */
export const cellRecipe = cva({
  base: {
    minWidth: "[0]",
    boxSizing: "border-box",
    borderStartStartRadius: "container",
    borderStartEndRadius: "container",
    borderEndStartRadius: "container",
    borderEndEndRadius: "container",
  },
  variants: {
    kind: {
      empty: {
        minHeight: "[192px]",
        backgroundColor: "bg.surface.strong",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        paddingBlock: "primitives.200",
        paddingInline: "primitives.200",
      },
      withEvents: {
        display: "flex",
        flexDirection: "column",
        backgroundColor: "bg.surface",
        borderBlockStartWidth: "[1px]",
        borderBlockEndWidth: "[1px]",
        borderInlineStartWidth: "[1px]",
        borderInlineEndWidth: "[1px]",
        borderBlockStartStyle: "solid",
        borderBlockEndStyle: "solid",
        borderInlineStartStyle: "solid",
        borderInlineEndStyle: "solid",
        borderBlockStartColor: "border",
        borderBlockEndColor: "border",
        borderInlineStartColor: "border",
        borderInlineEndColor: "border",
      },
    },
    expanded: {
      expanded: { height: "auto" },
      collapsed: { minHeight: "[192px]" },
    },
  },
  defaultVariants: {
    kind: "empty",
    expanded: "collapsed",
  },
});

/**
 * Recipe for the date-number badge.
 * Replaces `cx(dateNumberBoxStyle, today && dateNumberBoxTodayStyle)`.
 */
export const dateNumberBoxRecipe = cva({
  base: {
    width: "[24px]",
    height: "[24px]",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: "0",
  },
  variants: {
    todayHighlight: {
      today: {
        borderStartStartRadius: "full",
        borderStartEndRadius: "full",
        borderEndStartRadius: "full",
        borderEndEndRadius: "full",
        backgroundColor: "[rgba(0, 0, 0, 0.1)]",
      },
      default: {},
    },
  },
  defaultVariants: { todayHighlight: "default" },
});

/**
 * Recipe for the event list wrapper.
 * Replaces `cx(eventListStyle, singleResidencyEventListStyle?, expandedEventListStyle?)`.
 */
export const eventListRecipe = cva({
  base: {
    paddingBlock: "primitives.200",
    paddingInline: "primitives.100",
    display: "flex",
    flexDirection: "column",
    rowGap: "primitives.100",
  },
  variants: {
    count: {
      single: {
        flexGrow: 1,
        justifyContent: "center",
      },
      multiple: {},
    },
    disclosure: {
      expanded: {
        maxHeight: "[360px]",
        overflowY: "auto",
      },
      collapsed: {},
    },
  },
  defaultVariants: { count: "multiple", disclosure: "collapsed" },
});

/**
 * Recipe for the chevron icon wrapper.
 * Replaces `cx(chevronBaseStyle, chevronRotatedStyle)`.
 */
export const chevronRecipe = cva({
  base: {
    display: "inline-block",
    transitionProperty: "[transform]",
    transitionDuration: "[0.2s]",
    verticalAlign: "middle",
  },
  variants: {
    rotated: {
      rotated: { transform: "[rotate(180deg)]" },
      default: {},
    },
  },
  defaultVariants: { rotated: "default" },
});

export const dateNumberRowStyle = css({
  display: "flex",
  alignItems: "flex-start",
  paddingInline: "primitives.200",
  paddingBlockStart: "primitives.100",
  paddingBlockEnd: "primitives.0",
  width: "full",
  flexShrink: "0",
});

export const chipRootStyle = css({
  width: "full",
});

export const disclosureRootStyle = css({
  width: "full",
  color: "text.subtle",
  fontSize: "primitives.300",
});

export const disclosureInnerStyle = css({
  display: "inline-flex",
  alignItems: "center",
  columnGap: "primitives.50",
});

export const disclosureWrapperStyle = css({
  paddingInline: "primitives.100",
  paddingBlockEnd: "primitives.200",
});

export const eventItemStyle = css({
  display: "flex",
  flexDirection: "column",
  rowGap: "primitives.50",
});
